/**
 * Phase 10 — Pk Effectiveness campaign detail page.
 *
 * Procurement-friendly layout: header + KPI strip + 3 charts (failure-
 * mode breakdown, miss-distance distribution, intercept-time
 * distribution) + multi-sample 3D trajectory viewer with success/fail
 * colour and Pk uncertainty cloud overlay. Polls every 1.5 s while
 * the campaign is queued / running. "Generate report" downloads PDF.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { formatApiError } from "@/lib/formatApiError";
import { scrubPhaseLabel } from "@/lib/displayName";
import pkCampaigns from "@/api/pkCampaigns";
import PkTrajectory3D from "@/components/PkTrajectory3D";
import SensitivitySection from "@/components/pk/SensitivitySection";
import TargetPkSection from "@/components/pk/TargetPkSection";

const TAXONOMY_LABEL = {
    intercept: "Intercept",
    miss_outside_lethal_radius: "Miss outside lethal radius",
    timeout: "Timeout",
    out_of_energy: "Out of energy",
    track_loss: "Track loss",
    seeker_lock_failed: "Seeker lock failed",
    guidance_saturated: "Guidance saturated",
    target_escaped: "Target escaped",
    unknown: "Unknown",
};

function fmtPk(v) { return (v == null || Number.isNaN(v)) ? "—" : Number(v).toFixed(3); }
function fmtNum(v, d = 2) { return (v == null || Number.isNaN(v)) ? "—" : Number(v).toFixed(d); }

export default function PkCampaignDetailPage() {
    const { campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [err, setErr] = useState("");
    const [activeSampleIdx, setActiveSampleIdx] = useState(null);
    const [showAllSamples, setShowAllSamples] = useState(true);
    const [showCloud, setShowCloud] = useState(true);
    const [showAllFailures, setShowAllFailures] = useState(false);

    useEffect(() => {
        if (!campaignId) return;
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            try {
                const body = await pkCampaigns.get(campaignId);
                if (cancelled) return;
                setCampaign(body);
                setErr("");
                if (body.status === "queued" || body.status === "running") {
                    timer = setTimeout(tick, 1500);
                }
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e, "Failed to load campaign"));
            }
        };
        tick();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [campaignId]);

    const isTerminal = campaign && ["complete", "cancelled", "failed"].includes(campaign.status);
    const canCancel = campaign && !campaign.legacy && (campaign.status === "queued" || campaign.status === "running");

    const failureRows = useMemo(() => {
        if (!campaign?.failure_mode_counts) return [];
        return Object.entries(campaign.failure_mode_counts)
            .filter(([k, v]) => showAllFailures || v > 0 || k === "intercept")
            .sort((a, b) => b[1] - a[1]);
    }, [campaign, showAllFailures]);

    const histMiss = useMemo(
        () => buildHistogram(campaign?.miss_distance_distribution || [], 12),
        [campaign?.miss_distance_distribution],
    );
    const histIntc = useMemo(
        () => buildHistogram(campaign?.intercept_time_distribution || [], 12),
        [campaign?.intercept_time_distribution],
    );

    const onCancel = async () => {
        try {
            const body = await pkCampaigns.cancel(campaignId);
            setCampaign(body);
        } catch (e) {
            setErr(formatApiError(e, "Failed to cancel campaign"));
        }
    };

    const onDownloadReport = async (format) => {
        if (!campaignId) return;
        // Phase 14.10 hot-fix — fetch the PDF/DOCX as an axios blob so
        // the JWT auth interceptor at api/client.js:21 attaches the
        // Authorization header. The earlier anchor-href pattern hit
        // PM's 401 because native anchor navigation doesn't go through
        // the axios interceptor (only fetch / XHR do).
        try {
            const { blob, filename } =
                await pkCampaigns.downloadReport(campaignId, format);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Defer revoke so the browser has time to start the download.
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[PkCampaignDetailPage] report download failed", e);
        }
    };

    if (err && !campaign) {
        return (
            <div className="space-y-4" data-testid="pk-campaign-detail-page">
                <Link to="/pk-campaigns" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1">
                    <ArrowLeft className="size-4" /> All campaigns
                </Link>
                <div data-testid="pk-detail-error" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                    {err}
                </div>
            </div>
        );
    }
    if (!campaign) {
        return (
            <div data-testid="pk-detail-loading" className="flex items-center gap-2 text-muted-foreground py-12">
                <Loader2 className="size-4 animate-spin" /> Loading campaign…
            </div>
        );
    }
    const k = campaign.kpis || {};
    return (
        <div className="space-y-6" data-testid="pk-campaign-detail-page">
            {campaign.legacy && (
                <div
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                    data-testid="pk-detail-legacy-banner"
                >
                    <strong>LEGACY — read only.</strong> This campaign was
                    created before the unified workbench and is
                    preserved for archival viewing only. Edit, clone, re-run,
                    and delete affordances are disabled. Create a new unified
                    campaign at <Link to="/campaigns/new" className="underline">
                    /campaigns/new</Link>.
                </div>
            )}
            <Link to="/pk-campaigns" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1" data-testid="pk-detail-back">
                <ArrowLeft className="size-4" /> All campaigns
            </Link>
            <PageHeader
                title={scrubPhaseLabel(campaign.name)}
                subtitle={
                    <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-muted-foreground">{campaign.scenario_name || campaign.scenario_id}</span>
                        <StatusPill status={campaign.status} />
                        <Badge variant="outline" className="capitalize">{campaign.campaign_type}</Badge>
                    </span>
                }
                testId="pk-detail-header"
                actions={[
                    canCancel && (
                        <AlertDialog key="cancel">
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" data-testid="pk-detail-cancel-button" className="gap-2">
                                    <StopCircle className="size-4" /> Cancel
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="pk-detail-cancel-confirm">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Cancel this Pk campaign?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Already-completed Monte Carlo samples will be retained;
                                        the campaign will be marked as cancelled with the partial Pk.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="pk-detail-cancel-confirm-no">Keep running</AlertDialogCancel>
                                    <AlertDialogAction onClick={onCancel} data-testid="pk-detail-cancel-confirm-yes">
                                        Cancel campaign
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    ),
                    isTerminal && campaign.status === "complete" && (
                        <Button
                            key="report"
                            onClick={() => onDownloadReport("pdf")}
                            data-testid="pk-detail-report-pdf"
                            className="gap-2"
                        >
                            <FileText className="size-4" /> Generate report (PDF)
                        </Button>
                    ),
                    isTerminal && campaign.status === "complete" && (
                        <Button
                            key="docx"
                            variant="outline"
                            onClick={() => onDownloadReport("docx")}
                            data-testid="pk-detail-report-docx"
                            className="gap-2"
                        >
                            <Download className="size-4" /> DOCX
                        </Button>
                    ),
                ].filter(Boolean)}
            />

            {/* KPI strip */}
            <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
                data-testid="pk-detail-kpis"
            >
                <KpiCard label="Point Pk" value={fmtPk(k.point_pk)} testId="kpi-point-pk" />
                <KpiCard label="95% CI" value={`[${fmtPk(k.ci_lower)}, ${fmtPk(k.ci_upper)}]`} testId="kpi-ci" />
                <KpiCard label="Successes" value={`${k.success_count || 0} / ${k.sample_count || 0}`} testId="kpi-successes" />
                <KpiCard label="Failures" value={`${k.failure_count || 0} / ${k.sample_count || 0}`} testId="kpi-failures" />
                <KpiCard label="Intercept time mean" value={fmtNum(k.intercept_time_mean_s) + " s"} testId="kpi-int-time" />
                <KpiCard label="Miss-distance mean" value={fmtNum(k.miss_distance_mean_m) + " m"} testId="kpi-miss-mean" />
            </div>

            {/* Failure mode breakdown */}
            <Section title="Failure-mode breakdown" testId="pk-detail-failure-modes">
                <div className="space-y-1.5">
                    {failureRows.map(([cls, count]) => (
                        <FailureBar
                            key={cls}
                            label={TAXONOMY_LABEL[cls] || cls}
                            count={count}
                            total={k.sample_count || 1}
                            testId={`pk-failure-bar-${cls}`}
                            isSuccess={cls === "intercept"}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    className="mt-3 text-xs text-muted-foreground hover:underline"
                    onClick={() => setShowAllFailures((v) => !v)}
                    data-testid="pk-failure-toggle-all"
                >
                    {showAllFailures ? "Hide zero-count classes" : "Show all 9 classes"}
                </button>
            </Section>

            {/* Distributions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Miss-distance distribution (m)" testId="pk-detail-miss-dist">
                    <Histogram bins={histMiss} testId="hist-miss" />
                </Section>
                <Section title="Intercept-time distribution (s)" testId="pk-detail-int-dist">
                    <Histogram bins={histIntc} testId="hist-int" />
                </Section>
            </div>

            {/* Trajectory viewer */}
            <Section title="Trajectory viewer" testId="pk-detail-trajectory">
                <div className="flex items-center gap-3 mb-3 flex-wrap text-sm">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={showAllSamples}
                            onChange={(e) => setShowAllSamples(e.target.checked)}
                            data-testid="pk-toggle-all-samples"
                        />
                        Show all samples
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={showCloud}
                            onChange={(e) => setShowCloud(e.target.checked)}
                            data-testid="pk-toggle-cloud"
                        />
                        Pk uncertainty cloud
                    </label>
                    <span className="text-muted-foreground ml-auto">
                        Highlight sample
                        <select
                            className="ml-2 bg-transparent border border-ux-border-1 rounded px-2 py-1"
                            value={activeSampleIdx ?? ""}
                            onChange={(e) => setActiveSampleIdx(e.target.value === "" ? null : Number(e.target.value))}
                            data-testid="pk-sample-stepper"
                        >
                            <option value="">(none)</option>
                            {(campaign.samples || []).map((s) => (
                                <option key={s.sample_idx} value={s.sample_idx}>
                                    #{s.sample_idx} — {s.success ? "intercept" : s.failure_mode}
                                </option>
                            ))}
                        </select>
                    </span>
                </div>
                {/* Phase 14.10 hot-fix — Bug #2: only baseline-lane
                    campaigns persist per-sample 3D trajectories
                    (sensitivity / target_pk / geometry workers run
                    with `store_trajectory=False`). Filter samples
                    accordingly and surface a transparent empty-state
                    explanation otherwise so PM can see the panel was
                    rendered intentionally, not that it crashed. */}
                {(() => {
                    const trajSamples = (campaign.samples || []).filter(
                        (s) => Boolean(s.trajectory_path),
                    );
                    if (trajSamples.length > 0) {
                        return (
                            <PkTrajectory3D
                                campaignId={campaign.id}
                                samples={trajSamples}
                                showAllSamples={showAllSamples}
                                showUncertaintyCloud={showCloud}
                                activeIdx={activeSampleIdx}
                            />
                        );
                    }
                    if (campaign.status === "complete") {
                        return (
                            <div
                                data-testid="pk-trajectory-not-persisted"
                                className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100"
                            >
                                <div className="font-medium mb-1">
                                    Per-sample trajectories not persisted for
                                    this campaign type
                                </div>
                                <div className="text-amber-200/80 text-xs leading-relaxed">
                                    {campaign.campaign_type === "baseline"
                                        ? "This baseline campaign completed without writing per-sample trajectory artifacts (older run pre-dating per-sample trajectory persistence). Re-run to capture trajectory data."
                                        : `${campaign.campaign_type} lane campaigns track aggregate KPIs only — per-sample 3D trajectories are persisted only for baseline-lane campaigns. Re-run this scenario in baseline mode if you need to inspect individual trajectories.`}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div
                            data-testid="pk-trajectory-pending"
                            className="text-muted-foreground p-6 text-sm"
                        >
                            Trajectories will appear when the campaign
                            completes.
                        </div>
                    );
                })()}
            </Section>

            <SensitivitySection campaign={campaign} />

            <TargetPkSection campaign={campaign} />

            {err && (
                <div data-testid="pk-detail-error" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                    {err}
                </div>
            )}
        </div>
    );
}

function KpiCard({ label, value, testId }) {
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3"
            data-testid={testId}
        >
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{value}</div>
        </div>
    );
}

function Section({ title, children, testId }) {
    return (
        <section data-testid={testId}>
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-2">
                {title}
            </h2>
            <div className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4">
                {children}
            </div>
        </section>
    );
}

function FailureBar({ label, count, total, testId, isSuccess }) {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
        <div className="grid grid-cols-12 items-center gap-2 text-sm" data-testid={testId}>
            <div className="col-span-4 text-muted-foreground capitalize">{label}</div>
            <div className="col-span-6 h-3 bg-ux-surface-2 rounded">
                <div
                    className="h-full rounded"
                    style={{
                        width: `${pct.toFixed(1)}%`,
                        background: isSuccess ? "#34d399" : "#f87171",
                    }}
                />
            </div>
            <div className="col-span-2 text-right tabular-nums">
                {count} ({pct.toFixed(0)}%)
            </div>
        </div>
    );
}

function Histogram({ bins, testId }) {
    if (!bins || !bins.length) {
        return (
            <div data-testid={`${testId}-empty`} className="text-muted-foreground text-sm py-6 text-center">
                No samples available yet.
            </div>
        );
    }
    const maxCount = Math.max(...bins.map((b) => b.count)) || 1;
    return (
        <div className="flex items-end gap-1 h-32" data-testid={testId}>
            {bins.map((b, i) => (
                <div
                    key={i}
                    className="flex-1 bg-indigo-400/40 hover:bg-indigo-400/60 rounded-t"
                    style={{ height: `${(b.count / maxCount) * 100}%` }}
                    title={`${b.lo.toFixed(1)}–${b.hi.toFixed(1)}: ${b.count}`}
                    data-testid={`${testId}-bar-${i}`}
                />
            ))}
        </div>
    );
}

function buildHistogram(values, binCount = 12) {
    if (!Array.isArray(values) || values.length === 0) return [];
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const span = hi - lo || 1;
    const w = span / binCount;
    const bins = Array.from({ length: binCount }, (_, i) => ({
        lo: lo + i * w,
        hi: lo + (i + 1) * w,
        count: 0,
    }));
    for (const v of values) {
        const idx = Math.min(binCount - 1, Math.floor((v - lo) / w));
        bins[idx].count += 1;
    }
    return bins;
}
